import { ReactNode } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { Link } from 'react-router';
import { ActionTone } from './actionTone';

interface Props {
  value: ReactNode;
  label: ReactNode;
  detail?: ReactNode;
  to: string;
  tone?: ActionTone;
  isLoading?: boolean;
  isError?: boolean;
}

export const ActionEntry = ({
  value,
  label,
  detail,
  to,
  tone = 'default',
  isLoading,
  isError,
}: Props) => {
  const accent = tone === 'default' ? 'primary' : tone;

  return (
    <Stack
      component={Link}
      to={to}
      direction="row"
      alignItems="center"
      gap={2}
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        pl: 2.5,
        pr: 2,
        py: 1.75,
        borderRadius: 2,
        textDecoration: 'none',
        color: 'text.primary',
        backgroundColor: theme.vars.palette.background.paper,
        transition: 'box-shadow 120ms ease, transform 120ms ease',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 5,
          backgroundColor: theme.vars.palette[accent].main,
        },
        '&:hover': {
          boxShadow: theme.shadows[1],
          transform: 'translateY(-2px)',
        },
      })}
    >
      <Box sx={{ minWidth: 32, display: 'flex', justifyContent: 'center' }}>
        {isLoading ? (
          <CircularProgress size={20} />
        ) : isError ? (
          <ErrorOutlineRoundedIcon color="error" />
        ) : (
          <Typography variant="h4" fontWeight={800} lineHeight={1} color={`${accent}.main`}>
            {value}
          </Typography>
        )}
      </Box>

      <Stack
        direction="row"
        alignItems="baseline"
        gap={1}
        flexWrap="wrap"
        sx={{ flexGrow: 1, minWidth: 0 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {label}
        </Typography>
        {detail && !isLoading && !isError && (
          <Typography variant="body2" color="text.secondary">
            {detail}
          </Typography>
        )}
      </Stack>

      <ChevronRightRoundedIcon sx={{ color: 'text.disabled', flexShrink: 0 }} />
    </Stack>
  );
};
